import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Terrain } from '../systems/Terrain';

export enum HumanoidState {
  WALKING = 'walking',
  FALLING = 'falling',
  BEING_ABDUCTED = 'being_abducted',
  RESCUED = 'rescued'
}

export class Humanoid implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private state: HumanoidState = HumanoidState.WALKING;
  private walkSpeed: number = 20;
  private walkDirection: number = 1; // 1 for right, -1 for left
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private abductorId: string | null = null;
  private fallSpeed: number = 100;
  private cameraX: number = 0; // Store camera position for rendering
  private cachedTerrain: Terrain | null = null; // Cache terrain reference

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: this.walkSpeed * this.walkDirection, y: 0 };
    this.size = { x: 8, y: 12 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    
    // Random initial direction and speed variation
    this.walkDirection = Math.random() > 0.5 ? 1 : -1;
    this.walkSpeed += Math.random() * 10 - 5; // ±5 speed variation
    this.velocity.x = this.walkSpeed * this.walkDirection;
  }

  public update(deltaTime: number): void {
    // Use cached terrain if available
    if (!this.cachedTerrain) return;
    // Update animation
    this.animationTime += deltaTime;
    if (this.animationTime >= 0.5) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 2;
    }

    switch (this.state) {
      case HumanoidState.WALKING:
        this.updateWalking(deltaTime, this.cachedTerrain);
        break;
      case HumanoidState.FALLING:
        this.updateFalling(deltaTime, this.cachedTerrain);
        break;
      case HumanoidState.BEING_ABDUCTED:
        this.updateBeingAbducted(deltaTime);
        break;
      case HumanoidState.RESCUED:
        this.updateRescued(deltaTime, this.cachedTerrain);
        break;
    }

    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  private updateWalking(deltaTime: number, terrain: Terrain): void {
    // Simple walking AI - change direction occasionally
    if (Math.random() < 0.001) { // 0.1% chance per frame to change direction
      this.walkDirection *= -1;
      this.velocity.x = this.walkSpeed * this.walkDirection;
    }

    // Keep on ground
    const groundY = terrain.getGroundHeight(this.position.x) - this.size.y;
    if (this.position.y < groundY) {
      this.position.y = groundY;
      this.velocity.y = 0;
    }
  }

  private updateFalling(deltaTime: number, terrain: Terrain): void {
    // Apply gravity
    this.velocity.y += this.fallSpeed * deltaTime;
    
    // Check if hit ground
    const groundY = terrain.getGroundHeight(this.position.x) - this.size.y;
    if (this.position.y >= groundY) {
      this.position.y = groundY;
      this.velocity.y = 0;
      this.state = HumanoidState.WALKING;
      this.velocity.x = this.walkSpeed * this.walkDirection;
    }
  }

  private updateBeingAbducted(deltaTime: number): void {
    // Humanoid is controlled by the Lander during abduction
    // This method might be called to update animation, but position is controlled externally
  }

  private updateRescued(deltaTime: number, terrain: Terrain): void {
    // Similar to falling, but humanoid is "saved"
    this.updateFalling(deltaTime, terrain);
    
    // Once landed, return to walking
    if (this.velocity.y === 0) {
      this.state = HumanoidState.WALKING;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    // Calculate screen position
    const screenX = this.position.x - this.cameraX;
    const screenY = this.position.y;
    
    // Don't render if off screen
    if (screenX < -20 || screenX > 820) return;

    const centerX = screenX + this.size.x / 2;
    const centerY = screenY + this.size.y / 2;

    this.drawHumanoidSprite(centerX, centerY);
  }

  private drawHumanoidSprite(centerX: number, centerY: number): void {
    // Simple humanoid sprite with walking animation
    let pattern: number[][];
    
    if (this.state === HumanoidState.BEING_ABDUCTED) {
      // Panic animation when being abducted
      pattern = [
        [0,1,1,0],
        [1,1,1,1],
        [0,1,1,0],
        [0,1,1,0],
        [1,0,0,1],
        [1,0,0,1]
      ];
    } else {
      // Normal walking animation
      pattern = this.animationFrame === 0 ? [
        [0,1,1,0],
        [1,1,1,1],
        [0,1,1,0],
        [0,1,1,0],
        [0,1,1,0],
        [1,0,0,1]
      ] : [
        [0,1,1,0],
        [1,1,1,1],
        [0,1,1,0],
        [0,1,1,0],
        [1,1,1,1],
        [0,1,0,1]
      ];
    }

    // Choose color based on state
    let color = '#55A049'; // Green for normal
    if (this.state === HumanoidState.BEING_ABDUCTED) {
      color = '#FFA500'; // Orange when being abducted
    } else if (this.state === HumanoidState.FALLING || this.state === HumanoidState.RESCUED) {
      color = '#BFCE72'; // Yellow when falling/rescued
    }

    // Draw the humanoid
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - 4 + col * 2;
          const y = centerY - 6 + row * 2;
          this.renderManager.fillRect(x, y, 2, 2, color);
        }
      }
    }
  }

  public startAbduction(abductorId: string): void {
    if (this.state !== HumanoidState.WALKING) return;
    
    this.state = HumanoidState.BEING_ABDUCTED;
    this.abductorId = abductorId;
    this.velocity.x = 0;
    this.velocity.y = -50; // Start moving up
  }

  public rescue(): void {
    this.state = HumanoidState.RESCUED;
    this.abductorId = null;
    this.velocity.x = 0;
    this.velocity.y = 0; // Will start falling in next update
  }

  public fall(): void {
    if (this.state === HumanoidState.BEING_ABDUCTED) {
      this.state = HumanoidState.FALLING;
      this.abductorId = null;
      this.velocity.x = 0;
      this.velocity.y = 0; // Will start falling due to gravity
    }
  }

  public isAbducted(): boolean {
    return this.state === HumanoidState.BEING_ABDUCTED;
  }

  public getState(): HumanoidState {
    return this.state;
  }

  public getAbductorId(): string | null {
    return this.abductorId;
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public setCameraX(cameraX: number): void {
    this.cameraX = cameraX;
  }

  public setTerrain(terrain: Terrain): void {
    this.cachedTerrain = terrain;
  }
}